/**
 * AI Scribe V2 — draft review
 *
 * What the provider sees once the session ends: the generated note on one tab,
 * the raw transcript on the other. The note itself is rendered by
 * `ScribeNoteCard`; this component only owns the tab and async states.
 *
 * @author [Rajkumar Rathod]
 */

import { Box, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { FlexCenterWrapper } from "@/components/DefaultNoDataWrapper";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import CustomTabs from "@/components/app/CustomTabs";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { PRIMITIVE, px } from "@/tokens/design-tokens";
import {
  SCRIBE_DRAFT_LABELS,
  SCRIBE_DRAFT_TAB,
  SCRIBE_DRAFT_TABS,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/ai-scriber/ai-scriber-constant";
import {
  ScribeDraftTab,
  ScribeNoteData,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/ai-scriber/ai-scriber-types";
import { toNoteSectionViews } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/ai-scriber/ai-scriber-utils";
import useScribeNoteStream from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/ai-scriber/hooks/use-scribe-note-stream";
import AiNotePreview, {
  AiNotePayload,
  isLegacyNoteType,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/ai-transcript-preview/AiNotePreview";
import { VisitNote } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/ActiveForm";
import { SCRIBE_V2_COLORS } from "../constants";
import { ScribeTranscriptLine } from "../types";
import { useCopyTranscript } from "../use-copy-transcript";
import CopyTranscriptButton from "./CopyTranscriptButton";
import ScribeNoteCard from "./ScribeNoteCard";
import TranscriptThread from "./TranscriptThread";

export interface ScribeDraftPanelProps {
  /** Segments captured during the session, shown on the transcription tab. */
  transcriptionSegments: ScribeTranscriptLine[];
  /** Bubbles the generated note up so the window can push it to the EHR. */
  onNoteReceived: (data: ScribeNoteData) => void;
  /** Re-runs note generation when the socket reports a failure. */
  onRetryNoteGeneration: () => void;
  /** Template the note was generated for — picks the preview to render. */
  noteType?: VisitNote;
  /** Identifies the scribe session; a new value discards the previous note. */
  sessionStartedAt: number;
}

const ScribeDraftPanel = ({
  transcriptionSegments,
  onNoteReceived,
  onRetryNoteGeneration,
  noteType,
  sessionStartedAt,
}: ScribeDraftPanelProps) => {
  const [activeTab, setActiveTab] = useState<ScribeDraftTab>(
    SCRIBE_DRAFT_TAB.NOTES
  );

  const { noteData, isLoading, isError, markRetrying, reset } =
    useScribeNoteStream({ onNoteReceived });
  const { copyTranscript, canCopy } = useCopyTranscript(transcriptionSegments);

  useEffect(() => {
    reset();
  }, [sessionStartedAt, reset]);

  if (isLoading) {
    return (
      <Flex
        flex={1}
        gap={1}
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: 0, backgroundColor: SCRIBE_V2_COLORS.noteCanvasBg }}
      >
        <Text sx={{ color: SCRIBE_V2_COLORS.textSecondary }}>
          {SCRIBE_DRAFT_LABELS.GENERATING}
        </Text>
        <CircularProgress size={px(PRIMITIVE.spacing[16])} color="inherit" />
      </Flex>
    );
  }

  if (isError) {
    return (
      <FlexCenterWrapper>
        <Text>{SCRIBE_DRAFT_LABELS.FAILED}</Text>
        <StyledButtonV2
          variant="primary"
          label={SCRIBE_DRAFT_LABELS.RETRY}
          onClick={() => {
            onRetryNoteGeneration();
            markRetrying();
          }}
        />
      </FlexCenterWrapper>
    );
  }

  const sections = toNoteSectionViews(noteData);
  const hasNote = sections.length > 0;

  return (
    <Flex
      flexDirection="column"
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: SCRIBE_V2_COLORS.noteCanvasBg,
      }}
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        px={px(PRIMITIVE.spacing[16])}
        sx={{
          flexShrink: 0,
          backgroundColor: SCRIBE_V2_COLORS.windowBg,
          borderBottom: `1px solid ${SCRIBE_V2_COLORS.noteCardBorder}`,
        }}
      >
        <CustomTabs
          value={activeTab}
          tabsData={SCRIBE_DRAFT_TABS}
          onChange={(value) => setActiveTab(value as ScribeDraftTab)}
        />
        {activeTab === SCRIBE_DRAFT_TAB.TRANSCRIPTION && (
          <CopyTranscriptButton onCopy={copyTranscript} disabled={!canCopy} />
        )}
      </Flex>

      {activeTab === SCRIBE_DRAFT_TAB.NOTES ? (
        /**
         * The SOAP templates return titled sections and keep the view already
         * built for them. Every other template returns a flat section map that
         * `AiNotePreview` flattens itself.
         */
        !isLegacyNoteType(noteType) ? (
          <AiNotePreview
            title={SCRIBE_DRAFT_LABELS.NOTE_CARD_TITLE}
            data={noteData as unknown as AiNotePayload}
            hideEmptySections
          />
        ) : hasNote ? (
          <ScribeNoteCard
            title={SCRIBE_DRAFT_LABELS.NOTE_CARD_TITLE}
            sections={sections}
          />
        ) : (
          <FlexCenterWrapper>
            <Text sx={{ color: SCRIBE_V2_COLORS.textSecondary }}>
              {SCRIBE_DRAFT_LABELS.NO_NOTE}
            </Text>
          </FlexCenterWrapper>
        )
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            p: px(PRIMITIVE.spacing[16]),
            backgroundColor: SCRIBE_V2_COLORS.windowBg,
          }}
        >
          {transcriptionSegments.length === 0 ? (
            <FlexCenterWrapper>
              <Text sx={{ color: SCRIBE_V2_COLORS.textSecondary }}>
                {SCRIBE_DRAFT_LABELS.NO_TRANSCRIPT}
              </Text>
            </FlexCenterWrapper>
          ) : (
            <TranscriptThread lines={transcriptionSegments} />
          )}
        </Box>
      )}
    </Flex>
  );
};

export default ScribeDraftPanel;
